import { Empty, Select, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { convertQueryObjToString, fetchSearchDebounce, infiniteScrollApiCall } from '@components-web/filters/helper';
import { formatOptionsForSelect, getErrorString, isNumericString } from '@utils/utils';
import Toast from '../toast';
import { ErrorMessage, Label, Skeleton } from '../index';
import cx from 'clsx';
import styles from '../select/index.module.scss';
import { FaChevronDown } from 'react-icons/fa6';
import { useGetRequest } from '@hooks';
import SelectChip from '../select/select-chip';

const InfiniteSelect = ({
  placeholder = '',
  loading = false,
  fetchKey = '',
  searchKey = '',
  queryKey = '',
  customRender = '',
  onChange = () => {},
  fetch = async () => {},
  required = false,
  labelSize = '',
  labelClassName = '',
  label = '',
  errorMsg = '',
  className,
  selectSize = 'large',
  mode = 'multiple',
  defaultValue = null,
  handleBlur = () => {},
  disabled = false,
  resetValue = false,
  setter = () => {},
  setAsSlug = false,
  filterKey = '',
  searchEnabled = true,
  capitalizeLabel = false,
  allowClear = false,
  clearSelect = () => {},
}) => {
  const [options, setOptions] = useState([]);
  const [scrollLoading, setScrollLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState(mode === 'single' ? undefined : []);
  const [pagination, setPagination] = useState(null);
  const [fetchedTillPage, setFetchedTillPage] = useState({ current_page: 0 });

  const functionsByMode = {
    single: {
      initialValue: () => undefined,
      value: () =>
        selectedValue
          ? {
              ...selectedValue,
              label: selectedValue?.title ?? selectedValue?.name ?? '',
              value: setAsSlug ? selectedValue?.slug : selectedValue?.id,
            }
          : undefined,
      setInitialValues: ({ list }) => {
        const newValue = list.find((item) =>
          setAsSlug ? item?.slug === defaultValue : item?.id === parseInt(defaultValue)
        );
        setSelectedValue(newValue);
        setter([newValue]);
      },
      onSelect: ({ value }) => {
        setSelectedValue(value);
      },
      clearSelect: () => {
        setSelectedValue({});
      },
      onDeselect: ({ item }) => {
        setSelectedValue({});
      },
      handleChange: ({ e }) => {
        onChange(options.find((item) => item.id === e));
      },
    },
    multiple: {
      initialValue: () => [],
      value: () => formatOptionsForSelect({ list: selectedValue, setAsSlug, capitalizeLabel }),
      setInitialValues: ({ list }) => {
        let optionIds = defaultValue.split(',');
        if (isNumericString(optionIds[0])) {
          optionIds = optionIds.map((option) => parseInt(option));
        }
        const newValue = list.filter((item) => optionIds.includes(setAsSlug ? item?.slug : item?.id));
        setSelectedValue(newValue);
        setter(newValue);
      },
      onSelect: ({ value }) => {
        const newValue = [...selectedValue, value];
        setSelectedValue(newValue);
      },
      clearSelect: () => {
        setSelectedValue([]);
      },
      onDeselect: ({ item }) => {
        const filteredArray = selectedValue?.filter((value) => value.id !== item);
        setSelectedValue([...filteredArray]);
        filteredArray?.length === 0 && setSelectedValue([]);
      },
      handleChange: ({ e }) => {
        let optionArray = options.length ? options : selectedValue;
        onChange(optionArray.filter((item) => e.includes(setAsSlug ? item.id : item.slug)));
      },
    },
  };

  const prefill = async () => {
    if (!defaultValue) {
      setSelectedValue(functionsByMode[mode].initialValue());
      return [];
    }
    if (options.length) return [];
    const {
      list,
      current_page = 1,
      next_page = null,
    } = await infiniteScrollApiCall({
      api: fetch,
      query:
        queryKey.length && (defaultValue.length || isNumericString(defaultValue))
          ? convertQueryObjToString({ [queryKey]: defaultValue })
          : '',
      key: fetchKey,
      page: 1,
    });
    setFetchedTillPage({ current_page, next_page });
    setPagination({ current_page, next_page });
    const formattedOptions = formatOptionsForSelect({ list, setAsSlug, capitalizeLabel });
    setOptions(formattedOptions);
    functionsByMode[mode].setInitialValues({ list });
    return formattedOptions ?? [];
  };

  const { loading: initialLoading } = useGetRequest({
    key: [(filterKey ?? label) + 'infiniteScroll', defaultValue],
    fetch: prefill,
  });

  useEffect(() => {
    if (resetValue) {
      setSelectedValue(functionsByMode[mode].initialValue());
      setOptions([]);
      setPagination(null);
      setFetchedTillPage({ current_page: 0 });
    }
  }, [resetValue]);

  const fetchScrollOptions = async ({ page, query = '', setScrollLoading = () => {} }) => {
    try {
      setFetchLoading(true);
      setScrollLoading(true);
      if (fetch && page) {
        const {
          list,
          current_page = 1,
          next_page = null,
        } = await infiniteScrollApiCall({
          api: fetch,
          query: query,
          key: fetchKey,
          page: query === '' && fetchedTillPage?.next_page > pagination?.next_page ? fetchedTillPage?.next_page : page,
        });

        if (query === '' && current_page > fetchedTillPage?.current_page)
          setFetchedTillPage({ current_page, next_page });

        setPagination({ current_page, next_page });
        if (current_page != pagination?.current_page || pagination?.current_page === 1) {
          const oldOptionIds = options.map((option) => option?.id);
          const optionsToAppend = list.filter((item) => !oldOptionIds.includes(item?.id));
          const newData = formatOptionsForSelect({
            list: [...options, ...optionsToAppend],
            setAsSlug,
            capitalizeLabel,
          });
          setOptions(newData);
          setter(newData);
        }
      }
    } catch (error) {
      Toast.error(getErrorString(error));
    } finally {
      setFetchLoading(false);
      setScrollLoading(false);
    }
  };

  const handleInfiniteScroll = (event) => {
    if (scrollLoading) return;
    const target = event.target;
    const isScrollEndOfPage = target.scrollTop + target.offsetHeight >= target.scrollHeight - 50;
    const nextPage = pagination.next_page;
    if (isScrollEndOfPage && nextPage) {
      fetchScrollOptions({ page: nextPage, setScrollLoading });
    }
  };

  const handleInputChange = async (value) => {
    if (!value?.length) fetchSearchDebounce({ api: () => fetchScrollOptions({ page: fetchedTillPage?.next_page }) });
    else {
      const query = `${searchKey}=${value}`;
      fetchSearchDebounce({ api: fetchScrollOptions, query });
    }
  };

  const filterOption = (input, option) => {
    return typeof option?.label === 'string'
      ? (option?.label ?? '').toLowerCase().includes(input.trim().toLowerCase())
      : true;
  };

  if (loading) return <Skeleton height={40} />;
  return (
    <div>
      {label && (
        <Label size={labelSize} className={labelClassName} required={required}>
          {label}
        </Label>
      )}
      <Select
        className={cx(styles.select, className)}
        value={functionsByMode[mode].value()}
        maxTagTextLength={10}
        maxTagCount={'responsive'}
        labelRender={(props) => {
          return <p style={{ color: 'var(--ant-color-primary)', fontWeight: 600 }}>{props?.label}</p>;
        }}
        tagRender={SelectChip}
        options={
          scrollLoading
            ? [...options, { label: <Spin size="small" style={{ display: 'block', margin: '4px auto' }} /> }]
            : options
        }
        placeholder={placeholder}
        allowClear={allowClear}
        mode={mode}
        filterOption={filterOption}
        status={errorMsg && 'error'}
        notFoundContent={
          fetchLoading || initialLoading ? (
            <Spin size="small" style={{ display: 'block', margin: '4px auto' }} />
          ) : (
            options?.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )
        }
        loading={scrollLoading}
        size={selectSize}
        suffixIcon={<FaChevronDown color="var(--gray-500)" />}
        onClear={() => {
          clearSelect();
        }}
        onSelect={(value, option) => {
          functionsByMode[mode].onSelect({ value: option });
          fetchScrollOptions({ page: fetchedTillPage?.next_page });
        }}
        onDeselect={(value) => {
          functionsByMode[mode].onDeselect({ item: value });
          fetchScrollOptions({ page: fetchedTillPage?.next_page });
        }}
        onSearch={(value) => {
          handleInputChange(value);
        }}
        onPopupScroll={(event) => {
          handleInfiniteScroll(event);
        }}
        onDropdownVisibleChange={(visible) => {
          visible && fetchScrollOptions({ page: pagination?.current_page ?? 1 });
        }}
        onChange={(e, items) => {
          e && functionsByMode[mode].handleChange({ e });
        }}
        showSearch={searchEnabled}
        onBlur={handleBlur}
        disabled={disabled}
        {...(customRender ? { optionRender: (item) => customRender(item, isSecondaryLanguage) } : {})}
      />
      {errorMsg && <ErrorMessage errorMsg={errorMsg} />}
    </div>
  );
};
export default InfiniteSelect;
